import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { task } from 'ember-concurrency';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';

interface SubscriptionOption extends Option {
  id: string;
}

interface AutocompleteResult {
  brand_code: string;
  description: string;
  hotel_id: string | undefined;
  market_id: string | undefined;
  name: string;
  products: string[] | undefined;
  subscription_type: string | undefined;
}

interface AutocompleteMeta {
  results_count: number;
  results_per_page: number;
}

interface BaseSubscriptionSelectSignature {
  Args: Record<string, unknown>;
}

export default class BaseSubscriptionSelect<
  S extends BaseSubscriptionSelectSignature = BaseSubscriptionSelectSignature,
> extends Component<S> {
  @service('ajax') declare ajaxService: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  // eslint-disable-next-line ember/no-unused-services -- used by subclasses in templates
  @service declare intl: Services['intl'];

  @tracked options: SubscriptionOption[] = [];
  @tracked page = 0;
  @tracked searchQuery = '';
  @tracked totalPages = 0;
  @tracked totalResults = 0;

  fetchSubscriptionsTask = task(
    { restartable: true },
    async (): Promise<SubscriptionOption[]> => {
      try {
        const { meta, results } = await this.ajaxService.request<{
          meta: AutocompleteMeta;
          results: AutocompleteResult[];
        }>('autocomplete/subscription_new_user/', {
          data: this.fetchParams,
          method: 'GET',
        });

        this.totalPages = Math.ceil(meta.results_count / meta.results_per_page);
        // Only the unfiltered count is the total, a search narrows results_count.
        if (this.searchQuery === '') {
          this.totalResults = meta.results_count;
        }

        return this.hotelAndMarketService
          .formatAutocompleteResults(results)
          .filter((result) => result.hotel_id !== undefined)
          .map((result) => ({
            badge: result.subscription_type,
            id: result.hotel_id!,
            label: result.name,
            unsafe_subLabel: result.products?.join(', '),
          }));
      } catch {
        this.totalPages = 0;
        return [];
      }
    },
  );

  loadMoreTask = task({ drop: true }, async (): Promise<void> => {
    if (this.page + 1 >= this.totalPages) {
      return;
    }

    this.page += 1;
    const newOptions = await this.fetchSubscriptionsTask.perform();
    this.options = [...this.options, ...newOptions];
  });

  searchTask = task(
    { restartable: true },
    async (query: string, debounce = true): Promise<void> => {
      if (debounce) {
        await timeout();
      }

      this.searchQuery = query;
      this.page = 0;
      this.options = await this.fetchSubscriptionsTask.perform();
    },
  );

  protected get fetchParams(): Record<string, string | number> {
    return { page: this.page, q: this.searchQuery };
  }

  @action
  onOpen(): void {
    void this.searchTask.perform('', false);
  }

  @action
  onSearch(query: string): void {
    void this.searchTask.perform(query);
  }
}

export type { AutocompleteMeta, AutocompleteResult, SubscriptionOption };
